import React from 'react';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { useSAProxyNavigation } from '../../hooks/useSAProxyNavigation';
import { getActiveInvestorJourney } from '../../services/investorJourneyScope';
import { type InvestorDashboardDto } from '../../services/investor.service';
import { JOURNEY_STEPS, isStepDone, isStepLocked, type JourneyStepKey } from '../../utils/journeySteps';

interface Props {
  dashboardData: InvestorDashboardDto | null;
  compact?: boolean;
}

export const PremiumJourneyStepper: React.FC<Props> = ({ dashboardData, compact }) => {
  const { navigate: saNavigate } = useSAProxyNavigation();
  const { pathname } = useLocation();

  if (!dashboardData) return null;


  const isDoneFor = (key: string) => isStepDone(key as JourneyStepKey, dashboardData);
  const stepKeys = JOURNEY_STEPS.map((s) => s.key);
  const journeySteps = JOURNEY_STEPS;

  const matchedStep = journeySteps.find((step) => {
    if (step.path === '/investor/journey') {
      return pathname.startsWith('/investor/journey');
    }
    return pathname === step.path;
  });

  const currentStepKey = matchedStep ? matchedStep.key : (stepKeys.find((k) => !isDoneFor(k)) ?? 'information');

  // Laravel parity: In-person Verification is 'later' (locked) until every onboarding
  // document is APPROVED — a done verification stays reachable for viewing.
  const isLockedFor = (key: string) => isStepLocked(key as JourneyStepKey, dashboardData);
  const goTo = (step: { key: string; path: string; label: string }) => {
    if (isLockedFor(step.key)) {
      toast.info('In-person Verification unlocks once your onboarding documents are approved.');
      return;
    }
    // Multi-journey scope (user 2026-08-25): the Information route is the only step
    // carrying the journey in its URL — a bare /investor/journey cleared the active
    // scope and dropped the investor back to the PRIMARY journey (and every later
    // tab followed it there). Keep the current journey in the link. The other step
    // routes stay journey-less by design; their pages read the same scope.
    if (step.key === 'information') {
      const jid = getActiveInvestorJourney();
      saNavigate(jid ? `/investor/journey/${encodeURIComponent(jid)}` : step.path, { state: { visit: true } });
      return;
    }
    // visit: true = the investor chose this step; the step pages never auto-forward a deliberate visit.
    saNavigate(step.path, { state: { visit: true } });
  };

  const stepperContent = (
    <div className="flex justify-between gap-2.5 relative">
      {journeySteps.map((step, idx) => {
        const isDone = isDoneFor(step.key);
        const isCurrent = currentStepKey === step.key;

        return (
          <div key={step.key} className="flex flex-col gap-0 items-center group relative">
            {/* Connector Line */}
            {idx < journeySteps.length - 1 && (
              <div className="absolute top-[14px] left-[50%] w-full h-[2px] bg-white/20 z-10 group-last:hidden">
                <div
                  className={`h-full bg-success-500 transition-all duration-500 ${isDone ? 'w-full' : 'w-0'}`}
                />
              </div>
            )}

            {/* Node */}
            <button
              onClick={() => goTo(step)}
              title={isLockedFor(step.key) ? 'Unlocks once your onboarding documents are approved' : undefined}
              className={`relative w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 z-10 mb-1
                ${isDone ? 'bg-success-500 border-0' : isCurrent ? 'bg-[#b0842b] border-0' : 'bg-[#7facb7] border border-[#325f6a]'}
                ${isLockedFor(step.key) ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110 active:scale-95'} shadow-sm
              `}
            >
              {isDone ? (
                <i className="bi bi-check-lg text-white text-base font-bold"></i>
              ) : (
                <i className={`${step.icon} text-xs ${isCurrent ? 'text-white font-bold' : 'text-[#325f6a]'}`}></i>
              )}

              {/* Active Pulse */}
              {isCurrent && (
                <div className="absolute -inset-1 border-2 border-[#b0842b] rounded-full animate-ping opacity-30" />
              )}
            </button>

            {/* Label */}
            <button
              onClick={() => goTo(step)}
              title={isLockedFor(step.key) ? 'Unlocks once your onboarding documents are approved' : undefined}
              className={`text-[10px] font-medium transition-colors
                ${isDone ? 'text-white/80 hover:text-white' : isCurrent ? 'text-white font-bold' : 'text-white/50 hover:text-white/80'}
                ${isLockedFor(step.key) ? 'cursor-not-allowed' : ''}
              `}
            >
              {step.label}
            </button>
          </div>
        );
      })}
    </div>
  );

  if (compact) {
    return stepperContent;
  }

  return (
    <div 
      className="border border-neutral-200 rounded-md p-2 flex justify-between lg:flex-row items-center gap-6 mb-6 w-full"
      style={{ background: 'linear-gradient(90deg, rgba(5, 47, 85, 1), rgba(7, 87, 155, 1)), radial-gradient(circle at 82% 20%, rgba(28, 166, 217, 1), transparent 34%)' }}
    >
      {/* Journey Header */}
      <div className="flex flex-col gap-0 min-w-[120px]">
        <h2 className="text-white font-bold text-sm tracking-tight uppercase mb-0">Your Journey</h2>
      </div>

      {/* Stepper Content */}
      {stepperContent}
    </div>
  );
};
